<template>
  <div id="app" :class="{ selectionMode, insertMode }">
    <MainView />
    <ActionMenu />
    <AccountMenu />
    <TypeMenu />
    <DateMenu />
    <ListMenu />
    <ColorMenu />
  </div>
</template>

<style>
/* Global styles */
body {
  /* styling configuration */
  --bg-color-main: transparent;
  --bg-color-list: #FFFDF4ee;
  --bg-color-list-header: transparent;
  --fg-color-list-header: white;
  --bg-color-date-header: transparent;
  --bg-color-hover: rgba(0, 0, 0, 0.06);
  --bg-color-topbar: #FEFCF5b0;
  --bg-color-topbar-selection: var(--accent-color-l80);
  --link-color: var(--accent-color-l40);
  --cta-color: var(--accent-color-l80);
  --sidebar-bg-color: #FEFCF5b0;
  --sidebar-bg-color-floating: #FEFCF5f0;
  --accent-color-l90: #9ffa5e;
  --accent-color-l80: #82dd41;
  --accent-color-l70: #6abf2c;
  --accent-color-l60: #54a318;
  --accent-color-l50: #3a8704;
  --accent-color-l40: #2e6b02;

  --bg-color-card-default: #EEEADA;
  --card-color-default: #EEEADA;
  --card-color-red: #ff8888;
  --card-color-green: #b0f57f;
  --card-color-yellow: #fff67eff;
  --card-color-blue: #9fd4ffff;
  --card-color-violet: #c9adf4;
  --card-color-lightgrey: #e8e8e8;
  --card-color-lightred: #ffd9d9;
  --card-color-lightorange: #ffeacc;
  --card-color-lightyellow: #fffbcc;
  --card-color-lightgreen: #e1ffcc;
  --card-color-lightcyan: #ccfff6;
  --card-color-lightblue: #cceeff;
  --card-color-lightviolet: #f0e5ff;
  --card-color-lightpink: #ffe5f4;

  --text-color-primary: #362305;
  --text-opacity-secondary: 80%;
  --text-opacity-tertiary: 62%;
  --text-opacity-quaternary: 50%;
  --text-size-primary: 16px;
  --text-size-secondary: 90%;
  --text-size-tertiary: 85%;
  --icon-opacity: 70%;
  --icon-opacity-topbar: 85%;

  --topbar-border: 1px solid #DDD8C6;
  --border-color1: #bbbbbb;
  --card-border: none;
  --list-border: 1px solid #DDD8C6;
  --task-border-color: #bbbbbb;
  --task-header-underline: 2px solid #bbbbbb;
  --date-header-underline: 1px solid #555555;
  --task-border: 1px solid var(--task-border-color);
  --menu-border: 1px solid #d1d1d1;
  --selection-border: 2px solid #555555;
  --rounding-smaller: 8px;
  --rounding-larger: 16px;

  --basic-card-size: 40px;
  --subitem-size: 26px;
  --indent-size: max(var(--basic-card-size), 40px);
  --subitem-icon: 36px;
  --basic-padding: 8px;
  --basic-margin: 8px;
  --basic-gap: 4px;
  --min-button-height: 28px;
  --min-item-text-width: 200px;
  --medium-button-size: 32px;

  /* scrollbar styling for Chrome and Safari */
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.33);
  }
  /* scrollbar styling for Firefox */
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
  scrollbar-width: thin;
}

body,
button {
  font-family: Roboto, Noto Sans, -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  margin: 0;
  user-select: none;
  color: var(--text-color-primary);
  font-size: var(--text-size-primary);
}

button {
  border: none;
  border-radius: var(--rounding-smaller);
  background: transparent;
  cursor: pointer;
}
button:hover {
  background: var(--bg-color-hover);
}

.action {
  font-style: italic;
  opacity: var(--text-opacity-tertiary);
  font-size: var(--text-size-secondary);
}

.md-icon {
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  opacity: var(--icon-opacity);
  display: flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: "liga";
  -webkit-font-smoothing: antialiased;
}
.md-icon-outlined {
  font-family: "Material Icons Outlined";
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  opacity: var(--icon-opacity);
  display: flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: "liga";
  -webkit-font-smoothing: antialiased;
}
</style>

<script>
import MainView from "@/views/main.vue";
import selection from "@/selection";
import ActionMenu from "./components/ActionMenu.vue";
import AccountMenu from "./components/AccountMenu.vue";
import TypeMenu from "./components/TypeMenu.vue";
import settings from "@/synced-settings";
import DateMenu from "./components/DateMenu.vue";
import ListMenu from "./components/ListMenu.vue";
import ColorMenu from "./components/ColorMenu.vue";

const isMobile = "ontouchstart" in window || window.navigator.msMaxTouchPoints > 0;

export default {
  data() {
    return {
      isMobile,
      backdrop: false, // TODO always disable on router change
      editedCard: null,
      showSidebar: window.innerWidth > 920,
    };
  },
  components: {
    MainView,
    ActionMenu,
    AccountMenu,
    TypeMenu,
    DateMenu,
    ListMenu,
    ColorMenu,
  },
  computed: {
    selectionMode() {
      return !!selection.length;
    },
    insertMode() {
      return selection.insertMode;
    },
  },
  methods: {},
  mounted() {
    // TODO it would be better to have this as a redirect in routing config,
    // but we don't have access to settings there.
    if (this.$route.path === "/") {
      this.$router.push(settings.homeView);
    }
  },
};
</script>
